import React from "react";
import { store } from "@store/index";
import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import {
  createModel,
  updateModel,
  updateModelProp,
  updateModelPropWithAutoHandleAndValidate,
  removeModelContactLink,
  type GetModelOutput,
} from "@store/features/crm/crmSlice";
import {
  close as closeSideNavigation,
  updateParams,
} from "@store/features/layout/sideNavigationSlice";
import { toast } from "sonner";
import { open as openDialog } from "@store/features/layout/dialogSlice";
import { openDeleteModelDialog } from "../../../lib/openMigrationDialog";
import { SpanCol } from "skedyul-ui/ui";
import { trpc } from "@utils/api";
import type { ModelView } from "skedyul-core/features/crm";

type EditModelParams = {
  views?: ModelView[];
  viewsLoading?: boolean;
  viewsNeedRefresh?: boolean;
};

export const editModelViewSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }>,
): SideNavigationV2Props<PageV2Component> => {
  const { subdomain } = args.routerContext.params;
  const router = args.routerContext.router;

  const {
    crm: {
      model: { data: model, isLoading },
      communicationChannels: { data: communicationChannels },
    },
    sideNavigation: {
      right: { isOpen, params: sideParams },
    },
  } = args.state;

  const editModelParams = (sideParams || {}) as EditModelParams;
  const views = editModelParams.views || [];
  const viewsLoading = editModelParams.viewsLoading ?? false;
  const contactAssociationLinks = model?.contactAssociationLinks ?? [];

  const openChannelMapper = (opts?: {
    selectedChannelId?: string;
    selectedFieldMappings?: Record<string, string>;
  }) => {
    if (!model?.id) return;
    store.dispatch(
      openDialog({
        schema: "APP_MODEL_RESOURCE_INSTANCE_MAPPER",
        params: {
          resourceType: "channel",
          mode: opts?.selectedChannelId ? "edit" : undefined,
          lockedModelId: model.id,
          lockedModelName: model.name,
          lockedModelFields: (model.fields || []).map((f) => ({
            id: f.id,
            label: f.label,
            handle: f.handle,
            definition: f.definition?.handle,
          })),
          existingChannelLinks: contactAssociationLinks,
          selectedChannelId: opts?.selectedChannelId,
          selectedFieldMappings: opts?.selectedFieldMappings,
        },
      }),
    );
  };

  return {
    side: "right",
    isOpen,
    width: "700px",
    component: {
      component: "PageV2",
      props: {
        title: model?.name ? model.name : "Untitled Model",
        hideTitle: true,
        header: {
          icon: "Rocket",
          primaryAction: {
            isLoading,
            label: model?.id ? "Save Model" : "Create Model",
            handleClick: async () => {
              try {
                const latestModel = store.getState().crm.model.data;
                if (!latestModel || !latestModel.name || !latestModel.handle) {
                  toast.error("Please fill in all required fields");
                  return;
                }

                let response: {
                  meta: { requestStatus: string };
                  payload?: unknown;
                };

                if (!latestModel.id) {
                  response = await store.dispatch(
                    createModel({
                      name: latestModel.name,
                      handle: latestModel.handle,
                      labelTemplate: latestModel.labelTemplate || "{{ id }}",
                      description: latestModel.description || "",
                      imageEnabled: latestModel.imageEnabled ?? false,
                      imageSeedTemplate: latestModel.imageSeedTemplate || null,
                    }),
                  );
                } else {
                  response = await store.dispatch(
                    updateModel({
                      id: latestModel.id,
                      name: latestModel.name,
                      handle: latestModel.handle,
                      labelTemplate: latestModel.labelTemplate || "{{ id }}",
                      imageEnabled: latestModel.imageEnabled ?? false,
                      imageSeedTemplate: latestModel.imageSeedTemplate || null,
                      description: latestModel.description ?? "",
                    }),
                  );
                }

                if (response.meta.requestStatus === "fulfilled") {
                  store.dispatch(closeSideNavigation({ position: "right" }));
                  toast.success("Model saved");
                } else {
                  toast.error("Failed to save model");
                }
              } catch (error) {
                toast.error("Failed to save model");
              }
            },
          },
        },
        content: {
          structure: "sidebar",
          components: [
            {
              type: "Card",
              title: "Details",
              props: {
                header: {
                  title: "Details",
                  description: "",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  id: "model-details-form",
                  fields: [
                    {
                      component: "Input",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text",
                        id: "model-name",
                        label: "Name",
                        leftIcon: "Text",
                        value: model?.name,
                        placeholder: "Enter your unique model name",
                        helpText:
                          "This is the human readable name, is should be singular and in title case, e.g. 'Customer' or 'Location'",
                        handleChange: (ev) => {
                          void store.dispatch(
                            updateModelPropWithAutoHandleAndValidate({
                              key: "name",
                              value: ev.target.value,
                              subdomain,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Input",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text",
                        id: "model-handle",
                        label: "Handle",
                        leftIcon: "Link",
                        value: model?.handle,
                        isDisabled: model?.id ? true : false,
                        placeholder: "Enter your unique model handle",
                        helpText:
                          "This is the unique identifier for the model, it should be lowercase and in kebab-case, e.g. 'customer' or 'location'",
                        handleChange: (ev) => {
                          store.dispatch(
                            updateModelProp({
                              key: "handle",
                              value: ev.target.value,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Textarea",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        id: "model-description",
                        label: "Description",
                        value: model?.description,
                        placeholder: "Enter your model description",
                        helpText:
                          "This is the description of the model, we use this internally with agents to understand the model, describe in a few words what the entity is about",
                        handleChange: (ev) => {
                          store.dispatch(
                            updateModelProp({
                              key: "description",
                              value: ev.target.value,
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Appearance",
              props: {
                actions: [],
                header: {
                  title: "Appearance",
                  description: "Customize the appearance of the model",
                },
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Input",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text",
                        id: "labelTemplate",
                        label: "Label Template",
                        leftIcon: "Braces",
                        value: model?.labelTemplate,
                        placeholder: "{{ id }}",
                        helpText:
                          "A Liquid template for the label of the model, e.g. '{{ name }}', '{{ handle }}', '{{ id }}'",
                        handleChange: (ev) => {
                          store.dispatch(
                            updateModelProp({
                              key: "labelTemplate",
                              value: ev.target.value,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Checkbox",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        id: "imageEnabled",
                        label: "Enable Images",
                        helpText:
                          "Enable generated or uploaded images for instances of this model.",
                        checked: !!model?.imageEnabled,
                        handleChange: (v: boolean) => {
                          store.dispatch(
                            updateModelProp({
                              key: "imageEnabled",
                              value: !!v,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Input",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text",
                        id: "imageSeedTemplate",
                        label: "Image Seed Template",
                        leftIcon: "Braces",
                        value: model?.imageSeedTemplate || "",
                        placeholder: "{{ email }}",
                        helpText:
                          "Unique, stable template used to generate deterministic avatars (e.g. '{{ email }}').",
                        handleChange: (ev) => {
                          store.dispatch(
                            updateModelProp({
                              key: "imageSeedTemplate",
                              value: ev.target.value,
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Contactable",
              hidden: !model?.id || communicationChannels.length === 0,
              props: {
                header: {
                  title: "Contactable",
                  description:
                    "Link communication channels to enable messaging with records in this model.",
                },
                secondaryActions: [
                  {
                    label: "Link Channel",
                    variant: "outline" as const,
                    size: "sm" as const,
                    leftIcon: "Plus" as const,
                    handleClick: () => {
                      openChannelMapper();
                    },
                  },
                ],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "DataTable" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        data: contactAssociationLinks,
                        emptyMessage:
                          "No channels linked. Click 'Link Channel' to enable messaging.",
                        emptyActions: [
                          {
                            label: "Link Channel",
                            variant: "outline" as const,
                            size: "sm" as const,
                            handleClick: () => {
                              openChannelMapper();
                            },
                          },
                        ],
                        columns: [
                          {
                            id: "channel",
                            type: "rowLayout" as const,
                            header: "Channel",
                            accessorKey: "communicationChannel.name",
                            align: "left" as const,
                            components: [
                              {
                                component: "Text" as const,
                                generateProps: (row: {
                                  communicationChannel: { name: string } | null;
                                }) => ({
                                  children:
                                    row.communicationChannel?.name || "",
                                }),
                              },
                            ],
                          },
                          {
                            id: "identifier",
                            type: "rowLayout" as const,
                            header: "Phone/Email",
                            accessorKey: "communicationChannel.identifierValue",
                            align: "left" as const,
                            components: [
                              {
                                component: "Text" as const,
                                generateProps: (row: {
                                  communicationChannel: {
                                    identifierValue: string;
                                  } | null;
                                }) => ({
                                  children:
                                    row.communicationChannel?.identifierValue ||
                                    "",
                                }),
                              },
                            ],
                          },
                          {
                            id: "identifierField",
                            type: "rowLayout" as const,
                            header: "Identifier Field",
                            accessorKey: "identifierField.label",
                            align: "left" as const,
                            components: [
                              {
                                component: "Text" as const,
                                generateProps: (row: {
                                  identifierField: { label: string } | null;
                                }) => ({
                                  children: row.identifierField?.label || "",
                                }),
                              },
                            ],
                          },
                          {
                            id: "actions",
                            type: "rowLayout" as const,
                            header: "",
                            accessorKey: "id",
                            align: "right" as const,
                            components: [
                              {
                                component: "Button" as const,
                                generateProps: (row: {
                                  id: string;
                                  communicationChannelId: string;
                                }) => ({
                                  label: "Remove",
                                  variant: "ghost" as const,
                                  size: "sm" as const,
                                  handleClick: async () => {
                                    try {
                                      await trpc.apps.deleteChannelModelLink.mutate(
                                        {
                                          channelModelLinkInstanceId: row.id,
                                        },
                                      );
                                      store.dispatch(
                                        removeModelContactLink({
                                          communicationChannelId:
                                            row.communicationChannelId,
                                        }),
                                      );
                                      toast.success("Channel link removed");
                                    } catch (error) {
                                      console.error(
                                        "Failed to remove link:",
                                        error,
                                      );
                                      toast.error(
                                        "Failed to remove channel link",
                                      );
                                    }
                                  },
                                }),
                              },
                            ],
                          },
                        ],
                        onRowClick: (
                          row: NonNullable<
                            GetModelOutput["contactAssociationLinks"]
                          >[number],
                        ) => {
                          openChannelMapper({
                            selectedChannelId: row.communicationChannelId,
                            selectedFieldMappings: row.fieldMappings,
                          });
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Linked Apps",
              hidden:
                !model?.id ||
                !(
                  model as unknown as {
                    linkedApps?: Array<unknown>;
                  }
                )?.linkedApps?.length,
              props: {
                header: {
                  title: "Linked Apps",
                  description:
                    "Apps that have mapped this model to their resources",
                },
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "DataTable" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        data:
                          (
                            model as unknown as {
                              linkedApps?: Array<{
                                id: string;
                                appName: string;
                                appHandle: string;
                                installId: string;
                                resourceHandle: string;
                              }>;
                            }
                          )?.linkedApps || [],
                        emptyMessage:
                          "No apps have linked this model to their resources.",
                        columns: [
                          {
                            id: "appName",
                            type: "rowLayout" as const,
                            header: "App",
                            accessorKey: "appName",
                            align: "left" as const,
                            components: [
                              {
                                component: "Text" as const,
                                generateProps: (row: { appName: string }) => ({
                                  children: row.appName || "",
                                }),
                              },
                            ],
                          },
                          {
                            id: "resourceHandle",
                            type: "rowLayout" as const,
                            header: "Resource",
                            accessorKey: "resourceHandle",
                            align: "left" as const,
                            components: [
                              {
                                component: "Text" as const,
                                generateProps: (row: {
                                  resourceHandle: string;
                                }) => ({
                                  children: row.resourceHandle || "",
                                }),
                              },
                            ],
                          },
                        ],
                        onRowClick: (row: {
                          appHandle: string;
                          installId: string;
                          scope: "INTERNAL" | "SHARED";
                        }) => {
                          const url =
                            row.scope === "INTERNAL"
                              ? `/${subdomain}/developers/apps/${row.appHandle}/general`
                              : `/${subdomain}/settings/apps/${row.appHandle}/install/${row.installId}`;

                          store.dispatch(
                            closeSideNavigation({ position: "right" }),
                          );
                          setTimeout(() => {
                            router.push(url);
                          }, 0);
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Views",
              hidden: !model?.id,
              props: {
                actions: [
                  {
                    label: "Add View",
                    size: "sm" as const,
                    variant: "secondary" as const,
                    handleClick: () => {
                      if (!model?.id) return;
                      store.dispatch(
                        openDialog({
                          schema: "CREATE_LIST_VIEW",
                          params: {
                            modelId: model.id,
                            modelHandle: model.handle,
                            modelHandlePlural: model.handlePlural,
                            title: "",
                            path: "",
                            isDefault: false,
                            isTeamView: true,
                            isEditing: false,
                          },
                        }),
                      );
                    },
                  },
                ],
                header: {
                  title: "Views",
                  description:
                    "Create list views with different filters, layouts, and paths. Each model has one instance page for record details.",
                },
                form: {
                  formVersion: "v1",
                  fields: viewsLoading
                    ? [
                        {
                          component: "Text" as const,
                          span: 12 as SpanCol,
                          mdSpan: 12 as SpanCol,
                          lgSpan: 12 as SpanCol,
                          props: {
                            children: "Loading views...",
                            className: "text-muted-foreground text-sm",
                          },
                        },
                      ]
                    : views.length === 0
                      ? [
                          {
                            component: "Text" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              children:
                                "No views yet. Click 'Add View' to create a list view.",
                              className: "text-muted-foreground text-sm",
                            },
                          },
                        ]
                      : [
                          {
                            component: "DataTable" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              data: views,
                              emptyMessage: "No views found",
                              columns: [
                                {
                                  id: "title",
                                  type: "rowLayout" as const,
                                  header: "Title",
                                  accessorKey: "title",
                                  align: "left" as const,
                                  components: [
                                    {
                                      component: "Text" as const,
                                      generateProps: (
                                        row: ModelView,
                                        _index: number,
                                      ) => ({
                                        children:
                                          row.pageType === "INSTANCE"
                                            ? "-"
                                            : row.title,
                                        className: "font-medium",
                                      }),
                                    },
                                  ],
                                },
                                {
                                  id: "path",
                                  type: "rowLayout" as const,
                                  header: "Path",
                                  accessorKey: "path",
                                  align: "left" as const,
                                  components: [
                                    {
                                      component: "Text" as const,
                                      generateProps: (
                                        row: ModelView,
                                        _index: number,
                                      ) => ({
                                        children: `/${row.path}`,
                                        className:
                                          "text-muted-foreground text-sm font-mono",
                                      }),
                                    },
                                  ],
                                },
                                {
                                  id: "visibility",
                                  type: "rowLayout" as const,
                                  header: "Visibility",
                                  accessorKey: "visibility",
                                  align: "center" as const,
                                  components: [
                                    {
                                      component: "Badge" as const,
                                      generateProps: (
                                        row: ModelView,
                                        _index: number,
                                      ) =>
                                        row.pageType === "INSTANCE"
                                          ? {
                                              children: "-",
                                              variant: "outline" as const,
                                              size: "sm" as const,
                                            }
                                          : {
                                              children: row.visibility,
                                              variant:
                                                row.visibility === "team"
                                                  ? ("default" as const)
                                                  : ("secondary" as const),
                                              size: "sm" as const,
                                            },
                                    },
                                  ],
                                },
                                {
                                  id: "default",
                                  type: "rowLayout" as const,
                                  header: "Default",
                                  accessorKey: "isDefault",
                                  align: "center" as const,
                                  components: [
                                    {
                                      component: "Badge" as const,
                                      generateProps: (
                                        row: ModelView,
                                        _index: number,
                                      ) =>
                                        row.pageType === "INSTANCE"
                                          ? {
                                              children: "-",
                                              variant: "outline" as const,
                                              size: "sm" as const,
                                            }
                                          : {
                                              children: row.isDefault
                                                ? "Default"
                                                : "",
                                              variant: "outline" as const,
                                              size: "sm" as const,
                                              className: row.isDefault
                                                ? ""
                                                : "invisible",
                                            },
                                    },
                                  ],
                                },
                                {
                                  id: "actions",
                                  type: "rowLayout" as const,
                                  header: "",
                                  accessorKey: "id",
                                  align: "right" as const,
                                  components: [
                                    {
                                      component: "Button" as const,
                                      generateProps: (
                                        row: ModelView,
                                        _index: number,
                                      ) =>
                                        row.pageType === "INSTANCE" ||
                                        row.isDefault
                                          ? {
                                              size: "icon" as const,
                                              variant: "ghost" as const,
                                              leftIcon: "Trash2",
                                              className:
                                                "h-7 w-7 text-muted-foreground opacity-40",
                                              isDisabled: true,
                                              title:
                                                row.pageType === "INSTANCE"
                                                  ? "Instance pages cannot be deleted"
                                                  : "Default list pages cannot be deleted",
                                              handleClick: (
                                                e: React.MouseEvent<
                                                  | HTMLButtonElement
                                                  | HTMLAnchorElement
                                                >,
                                              ) => {
                                                e.stopPropagation();
                                              },
                                            }
                                          : {
                                              size: "icon" as const,
                                              variant: "ghost" as const,
                                              leftIcon: "Trash2",
                                              className:
                                                "h-7 w-7 text-destructive hover:text-destructive",
                                              handleClick: (
                                                e: React.MouseEvent<
                                                  | HTMLButtonElement
                                                  | HTMLAnchorElement
                                                >,
                                              ) => {
                                                e.stopPropagation();
                                                store.dispatch(
                                                  openDialog({
                                                    schema: "DELETE_LIST_VIEW",
                                                    params: {
                                                      pageId: row.id,
                                                      title: row.title,
                                                      path: row.path,
                                                      isDefault: row.isDefault,
                                                    },
                                                  }),
                                                );
                                              },
                                            },
                                    },
                                  ],
                                },
                              ],
                              onRowClick: (row: ModelView) => {
                                if (!model || row.pageType === "INSTANCE")
                                  return;
                                store.dispatch(
                                  openDialog({
                                    schema: "EDIT_LIST_VIEW",
                                    params: {
                                      modelId: model.id,
                                      modelHandle: model.handle,
                                      modelHandlePlural: model.handlePlural,
                                      pageId: row.id,
                                      title: row.title,
                                      path: row.path,
                                      icon: row.icon,
                                      isDefault: row.isDefault,
                                      isTeamView: row.visibility === "team",
                                      isEditing: true,
                                    },
                                  }),
                                );
                              },
                            },
                          },
                        ],
                },
              },
            },
            {
              type: "Card",
              title: "Danger Zone",
              hidden: !model?.id,
              props: {
                actions: [],
                header: {
                  title: "Danger Zone",
                  description: "Destructive actions that cannot be undone",
                },
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Alert" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        variant: "destructive" as const,
                        title: "Delete Model",
                        description:
                          "This will permanently delete the model and all its connections. This action cannot be undone.",
                      },
                    },
                    {
                      component: "Button" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        variant: "destructive" as const,
                        label: "Delete Model",
                        handleClick: () => {
                          if (!model?.id) return;
                          void openDeleteModelDialog(
                            model.id,
                            model.name || "Untitled Model",
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
      },
    },
  };
};

export const fetchModelViews = async (modelId: string) => {
  try {
    store.dispatch(
      updateParams({
        position: "right",
        params: { viewsLoading: true, viewsNeedRefresh: false },
      }),
    );

    const views = await trpc.crm.listModelViews.query({ modelId });

    store.dispatch(
      updateParams({
        position: "right",
        params: {
          viewsLoading: false,
          views: views as ModelView[],
        },
      }),
    );
  } catch (error) {
    console.error("Failed to fetch model views:", error);
    toast.error("Failed to load views");
    store.dispatch(
      updateParams({
        position: "right",
        params: { viewsLoading: false, views: [] },
      }),
    );
  }
};
